// Create qui recharge la page entière (« Nouveau contrat », « Nouvelle société », un
// sinistre…) : entre le clic sur « Suivant » et l'arrivée du parcours, l'écran ne bougeait
// pas du tout. Trois attentes s'enchaînent — le POST, la navigation posée par
// resources/_create.turbo_stream.erb (`window.top.location.href`), puis le rendu complet
// de la page d'arrivée — et le bouton n'avait ni spinner ni état désactivé.
//
// Deux temps, là où la sidebar n'en a qu'un :
//
//   0 ms    le bouton passe en attente. C'est là qu'est le curseur, donc l'accusé de
//           réception y est lisible immédiatement, et il ne déplace rien à l'écran.
//   300 ms  le squelette de la page d'arrivée, en overlay — et seulement si on attend
//           encore. Sur un create rapide, un squelette qui apparaît puis disparaît se lit
//           comme un raté, pas comme un chargement.
//
// Ce que le rechargement pleine page donne gratuitement : le navigateur garde l'ancien
// document peint jusqu'au premier paint du nouveau. L'overlay couvre donc les trois
// attentes d'une seule traite, sans relais ni second moule — contrairement au « Suivant »
// d'une étape de parcours, qui doit en enchaîner deux (cf. workflow_step_skeleton.ts).
//
// Le squelette n'est pas construit en JS : on clone le <template> que le serveur rend déjà
// pour la nav (NavSkeletonTemplatesComponent), et la variante à prendre est annoncée sur le
// formulaire par NavHelper#create_skeleton_data.

const FORM_SELECTOR = 'form[data-create-skeleton]';
// Même classe que le squelette instantané de la nav : même boîte (`inset: 0` dans
// `.app-shell__main`), même z-index, même papier — celui de la page, le formulaire s'effaçant
// dessous. Le modificateur porte le seul écart, le fondu d'apparition — ici le squelette arrive
// APRÈS un premier signal, donc le faire surgir d'un coup ferait sursauter l'écran.
const OVERLAY_CLASS = 'nav-instant-skeleton';
const OVERLAY_FADE_CLASS = 'nav-instant-skeleton--fade-in';
const SKELETON_DELAY_MS = 300;
// Filet large : l'overlay ne doit jamais rester si la réponse ne vient pas. Plus long que
// celui de la nav, parce qu'on couvre ici un POST ET un rechargement complet.
const SAFETY_TIMEOUT_MS = 20_000;
// Sonde de suggestions : le controller `search` resoumet CE formulaire à chaque frappe pour
// rafraîchir la liste de propositions, en glissant `source=stimulus` dans le corps. C'est le
// marqueur que ResourcesController lit pour ne rien enregistrer — rien ne bouge donc, et il
// n'y a rien à annoncer. Sans cette garde, le squelette recouvrirait le formulaire en pleine
// saisie (cf. le même piège dans workflow_step_skeleton.ts).
const PROBE_FIELD = 'source';
const PROBE_VALUE = 'stimulus';

let skeletonTimeout: number | null = null;
let safetyTimeout: number | null = null;
let busyForm: HTMLFormElement | null = null;
let busyButton: HTMLElement | null = null;

// Corps de la soumission : URLSearchParams en urlencoded, FormData en multipart — les deux
// répondent à `get`. On le lit plutôt que d'aller chercher le champ caché dans le DOM : le
// controller `search` le retire aussitôt après avoir soumis.
const isSuggestionProbe = (body: unknown): boolean => {
  const params = body as URLSearchParams | null;
  return typeof params?.get === 'function' && params.get(PROBE_FIELD) === PROBE_VALUE;
};

const overlayHost = () => document.querySelector<HTMLElement>('main.app-shell__main');

const clearTimers = () => {
  if (skeletonTimeout !== null) window.clearTimeout(skeletonTimeout);
  if (safetyTimeout !== null) window.clearTimeout(safetyTimeout);
  skeletonTimeout = null;
  safetyTimeout = null;
};

const reset = () => {
  clearTimers();
  document.querySelectorAll(`.${OVERLAY_FADE_CLASS}`).forEach((node) => node.remove());
  busyForm?.removeAttribute('aria-busy');
  busyButton?.removeAttribute('aria-busy');
  busyForm = null;
  busyButton = null;
};

// Tout l'habillage du bouton est en CSS : on ne fait que poser `aria-busy`, donc il garde sa
// boîte au pixel près — son libellé passe en transparent et un spinner vient au centre. Rien
// n'est reconstruit, donc rien à remettre en place, et un libellé sur mesure
// (`options[:submit_button_html]`) est couvert sans cas particulier.
const markBusy = (form: HTMLFormElement, submitter: HTMLElement | null) => {
  busyForm = form;
  busyButton = submitter || form.querySelector<HTMLElement>('button[type="submit"]');
  form.setAttribute('aria-busy', 'true');
  busyButton?.setAttribute('aria-busy', 'true');
};

const paintSkeleton = (variant: string) => {
  const template = document.querySelector<HTMLTemplateElement>(
    `template[data-nav-skeleton="${CSS.escape(variant)}"]`
  );
  const host = overlayHost();
  // Hors app-shell — iframe de souscription, Hotwire Native, page publique — le <template>
  // n'est pas rendu (cf. layouts/_body.html.erb, branche `sidebar_in_layout`). On s'en tient
  // alors au bouton, qui dit déjà « c'est parti ».
  if (!template || !host) return;
  if (host.querySelector(`.${OVERLAY_CLASS}`)) return;

  const overlay = document.createElement('div');
  overlay.className = `${OVERLAY_CLASS} ${OVERLAY_FADE_CLASS}`;
  overlay.setAttribute('aria-hidden', 'true');
  overlay.appendChild(template.content.cloneNode(true));
  host.appendChild(overlay);
};

export function initializeCreateSubmitTransition() {
  if ((window as any).__createSubmitTransitionInitialized) return;
  (window as any).__createSubmitTransitionInitialized = true;

  document.addEventListener('turbo:submit-start', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;

    const submission = (event as CustomEvent).detail?.formSubmission;
    if (isSuggestionProbe(submission?.body)) return;

    const variant = form.dataset.createSkeleton;
    if (!variant) return;

    markBusy(form, submission?.submitter || null);
    skeletonTimeout = window.setTimeout(() => paintSkeleton(variant), SKELETON_DELAY_MS);
    safetyTimeout = window.setTimeout(reset, SAFETY_TIMEOUT_MS);
  });

  // La réponse arrive. Deux issues, qui demandent l'inverse l'une de l'autre :
  //
  //   - le formulaire revient (erreurs de validation → `replace` de sa frame ; retour à
  //     l'index → `replace` de la frame d'index) : il faut rendre la main tout de suite ;
  //   - on sort en pleine page : le stream ne fait qu'ajouter un <script> à
  //     `flashes-container`, et l'overlay doit lui SURVIVRE. Le retirer ici rendrait le
  //     formulaire à l'écran pour toute la durée du rechargement, qui n'a pas encore
  //     commencé — soit exactement l'attente qu'on cherche à couvrir.
  //
  // On tranche sur la cible du stream : si elle désigne une frame qui CONTIENT le
  // formulaire, c'est que le formulaire revient. `flashes-container`, lui, est ailleurs
  // dans la page.
  document.addEventListener('turbo:before-stream-render', (event) => {
    if (!busyForm) return;

    const target = (event as CustomEvent).detail?.newStream?.getAttribute('target');
    if (!target) return;
    if (busyForm.closest(`turbo-frame#${CSS.escape(target)}`)) reset();
  });

  // Soumission qui n'aboutit pas (erreur réseau, 5xx) : rien ne sera rendu, il faut rendre
  // la main au formulaire.
  document.addEventListener('turbo:submit-end', (event) => {
    if ((event as CustomEvent).detail?.success === false) reset();
  });

  document.addEventListener('turbo:fetch-request-error', reset);

  // Filets. Aucun de ces événements ne se produit sur le chemin nominal — la sortie pleine
  // page n'est pas une visite Turbo — mais ils rattrapent le cas où la réponse emmènerait
  // finalement ailleurs, et `turbo:before-cache` garantit qu'un overlay n'entre jamais dans
  // le snapshot de la page qu'on quitte.
  document.addEventListener('turbo:before-cache', reset);
  document.addEventListener('turbo:load', reset);
  document.addEventListener('turbo:render', reset);
}
